import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Divider,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Checkbox,
    Stepper,
    Step,
    StepLabel,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import axios from 'axios';
import { createPricelistSupplier } from '../Service/productServices';
import { API_BASE_URL, API_DOCTOR_LIST } from 'helpers/urlHelper';
import * as XLSX from 'xlsx';

const AddSupplierPriceDrawer = ({ open, onClose, selectedPricelist, onPricelistAdded, products }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [productRows, setProductRows] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loadingDoctors, setLoadingDoctors] = useState(false);
    
    const [globalConfig, setGlobalConfig] = useState({
        userid: '',
        supplierid: '',
        minQuantity: 1,
        maxQuantity: '',
        startDate: '',
        endDate: '',
        currency: '€',
        priority: 0,
        active: true,
        couponCodes: ''
    });

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Cargar productos en filas
    useEffect(() => {
        if (open && products && products.length > 0) {
            const initialRows = products.map(product => ({
                productId: product._id,
                productCode: product.code,
                productName: product.name,
                originalPrice: product.price,
                customPrice: '',
                doctorId: '',
                selected: false
            }));
            setProductRows(initialRows);
        }
    }, [open, products]);

    // Cargar doctores desde la API
    useEffect(() => {
        const fetchDoctors = async () => {
            if (!open) return;
            
            setLoadingDoctors(true);
            try {
                const response = await axios.get(`${API_DOCTOR_LIST}`);
                console.log('📦 Doctors response:', response.data);
                
                let doctorsList = [];
                if (response.data.status && Array.isArray(response.data.data)) {
                    doctorsList = response.data.data;
                } else if (Array.isArray(response.data)) {
                    doctorsList = response.data;
                }
                
                console.log('✅ Doctors loaded:', doctorsList);
                setDoctors(doctorsList);
                
            } catch (error) {
                console.error('❌ Error loading doctors:', error);
                setErrorMessage('Error loading doctors');
                setDoctors([]);
            } finally {
                setLoadingDoctors(false);
            }
        };
        
        fetchDoctors();
    }, [open]);

    // 📥 EXPORTAR PLANTILLA EXCEL EN BLANCO
    const exportBlankTemplate = () => {
        const selectedRows = productRows.filter(row => row.selected);
        
        if (selectedRows.length === 0) {
            setErrorMessage('Please select at least one product to export');
            return;
        }

        const templateData = selectedRows.map(row => ({
            'Product Code *': row.productCode,
            'Product Name': row.productName,
            'Original Price': row.originalPrice,
            'Custom Price *': '',
            'Doctor ID *': '',
            'Doctor Name': '(See Doctors sheet)',
            'Min Quantity': 1,
            'Max Quantity': '',
            'Priority': 0,
            'Currency': '€',
            'Active': 'Yes',
            'Start Date (YYYY-MM-DD)': '',
            'End Date (YYYY-MM-DD)': '',
            'Coupon Codes (comma separated)': ''
        }));

        const doctorsData = doctors.map(d => ({
            'Doctor ID': d._id,
            'Doctor Name': d.name,
            'Email': d.email || ''
        }));

        const wb = XLSX.utils.book_new();
        
        const ws1 = XLSX.utils.json_to_sheet(templateData);
        XLSX.utils.book_append_sheet(wb, ws1, 'Products to Price');
        
        const ws2 = XLSX.utils.json_to_sheet(doctorsData);
        XLSX.utils.book_append_sheet(wb, ws2, 'Doctors');
        
        XLSX.writeFile(wb, `supplier_pricelist_template_${Date.now()}.xlsx`);
        
        setSuccessMessage(`✅ Template downloaded with ${selectedRows.length} products!`);
        setTimeout(() => setSuccessMessage(''), 3000);
    };

// 📤 IMPORTAR DESDE EXCEL - VERSIÓN CORREGIDA
const importFromExcel = async (file) => {
    setLoading(true);
    setErrorMessage('');
    
    try {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target.result);
            // ✅ Leer con raw: true para obtener los valores originales
            const workbook = XLSX.read(data, { type: 'array', cellDates: false });
            
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            // ✅ Obtener las columnas L y M (Start Date y End Date)
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            
            // ✅ Procesar manualmente cada celda de fecha
            for (let row = range.s.r + 1; row <= range.e.r; row++) {
                // Columna L (Start Date) - columna 11
                const startDateCell = worksheet[XLSX.utils.encode_cell({ r: row, c: 11 })];
                if (startDateCell && startDateCell.t === 'n') {
                    const excelDate = startDateCell.v;
                    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
                    const formatted = jsDate.toISOString().split('T')[0];
                    startDateCell.v = formatted;
                    startDateCell.t = 's'; // Cambiar tipo a string
                    console.log(`✅ Row ${row + 1} Start Date: ${excelDate} → ${formatted}`);
                }
                
                // Columna M (End Date) - columna 12
                const endDateCell = worksheet[XLSX.utils.encode_cell({ r: row, c: 12 })];
                if (endDateCell && endDateCell.t === 'n') {
                    const excelDate = endDateCell.v;
                    const jsDate = new Date((excelDate - 25569) * 86400 * 1000);
                    const formatted = jsDate.toISOString().split('T')[0];
                    endDateCell.v = formatted;
                    endDateCell.t = 's';
                    console.log(`✅ Row ${row + 1} End Date: ${excelDate} → ${formatted}`);
                }
            }
            
            // Ahora sí convertir a JSON
            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            
            console.log('📥 Imported data with parsed dates:', jsonData);
            
            const validRows = [];
            const errors = [];
            
            jsonData.forEach((row, index) => {
                if (!row['Product Code *']) {
                    errors.push(`Row ${index + 2}: Missing Product Code`);
                    return;
                }
                
                if (!row['Custom Price *'] || parseFloat(row['Custom Price *']) <= 0) {
                    errors.push(`Row ${index + 2}: Invalid Custom Price`);
                    return;
                }
                
                if (!row['Doctor ID *']) {
                    errors.push(`Row ${index + 2}: Missing Doctor ID`);
                    return;
                }
                
                const startDate = row['Start Date (YYYY-MM-DD)'] || undefined;
                const endDate = row['End Date (YYYY-MM-DD)'] || undefined;
                
                console.log(`📅 Row ${index + 2} - Start: ${startDate}, End: ${endDate}`);
                
                if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
                    errors.push(`Row ${index + 2}: End date (${endDate}) must be after start date (${startDate})`);
                    return;
                }
                
                validRows.push({
                    productCode: row['Product Code *'],
                    customPrice: parseFloat(row['Custom Price *']),
                    doctorId: row['Doctor ID *'],
                    minQuantity: parseInt(row['Min Quantity']) || 1,
                    maxQuantity: row['Max Quantity'] ? parseInt(row['Max Quantity']) : undefined,
                    priority: parseInt(row['Priority']) || 0,
                    currency: row['Currency'] || '€',
                    active: row['Active']?.toLowerCase() === 'yes' || row['Active'] === true,
                    startDate: startDate,
                    endDate: endDate,
                    couponCodes: row['Coupon Codes (comma separated)'] 
                        ? row['Coupon Codes (comma separated)'].split(',').map(c => c.trim())
                        : []
                });
            });
            
            if (errors.length > 0) {
                setErrorMessage(`❌ Validation errors:\n${errors.join('\n')}`);
                setLoading(false);
                return;
            }
            
            console.log('✅ Valid rows to import:', validRows);
            
            const promises = validRows.map(row => {
                const product = products.find(p => p.code === row.productCode);
                if (!product) {
                    throw new Error(`Product not found: ${row.productCode}`);
                }
                
                const dataToSend = {
                    productid: product._id,
                    userid: row.doctorId,
                    minQuantity: row.minQuantity,
                    maxQuantity: row.maxQuantity,
                    startDate: row.startDate,
                    endDate: row.endDate,
                    price: row.customPrice,
                    currency: row.currency,
                    priority: row.priority,
                    active: row.active,
                    couponCodes: row.couponCodes,
                    conditions: ''
                };
                
                console.log('📤 Sending pricelist data:', dataToSend);
                return createPricelistSupplier(dataToSend);
            });
            
            await Promise.all(promises);
            
            setSuccessMessage(`✅ ${validRows.length} pricelists imported successfully!`);
            
            setTimeout(() => {
                onPricelistAdded();
                onClose();
                resetForm();
            }, 1500);
        };
        
        reader.readAsArrayBuffer(file);
        
    } catch (error) {
        console.error('❌ Import error:', error);
        setErrorMessage(error.message || 'Error importing file');
    } finally {
        setLoading(false);
    }
};

    const handleGlobalConfigChange = (e) => {
        const { name, value, checked, type } = e.target;
        setGlobalConfig(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRowChange = (index, field, value) => {
        setProductRows(prev => {
            const newRows = [...prev];
            newRows[index] = {
                ...newRows[index],
                [field]: value
            };
            return newRows;
        });
    };

    const handleSelectAll = (checked) => {
        setProductRows(prev => prev.map(row => ({
            ...row,
            selected: checked
        })));
    };

    const handleDeleteRow = (index) => {
        setProductRows(prev => prev.filter((_, i) => i !== index));
    };

    const validateStep1 = () => {
        const selectedRows = productRows.filter(row => row.selected);
        
        if (selectedRows.length === 0) {
            setErrorMessage('Please select at least one product');
            return false;
        }

        for (let row of selectedRows) {
            if (!row.customPrice || parseFloat(row.customPrice) <= 0) {
                setErrorMessage(`Product "${row.productName}" must have a valid price`);
                return false;
            }
            if (!row.doctorId) {
                setErrorMessage(`Product "${row.productName}" must have a doctor selected`);
                return false;
            }
        }

        return true;
    };

    const validateStep2 = () => {
        if (globalConfig.minQuantity < 1) {
            setErrorMessage('Min quantity must be at least 1');
            return false;
        }

        if (globalConfig.maxQuantity && parseInt(globalConfig.maxQuantity) < parseInt(globalConfig.minQuantity)) {
            setErrorMessage('Max quantity must be greater than min quantity');
            return false;
        }

        if (globalConfig.startDate && globalConfig.endDate) {
            if (new Date(globalConfig.endDate) < new Date(globalConfig.startDate)) {
                setErrorMessage('End date must be after start date');
                return false;
            }
        }

        return true;
    };

    const handleNext = () => {
        setErrorMessage('');
        if (activeStep === 0) {
            if (validateStep1()) {
                setActiveStep(1);
            }
        }
    };

    const handleBack = () => {
        setActiveStep(0);
        setErrorMessage('');
    };

const handleSubmit = async () => {
    if (!validateStep2()) {
        return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
        const selectedRows = productRows.filter(row => row.selected);
        
        const promises = selectedRows.map(row => {
            const dataToSend = {
                productid: row.productId,
                userid: row.doctorId || null,
                supplierid: globalConfig.supplierid || null,
                minQuantity: parseInt(globalConfig.minQuantity),
                maxQuantity: globalConfig.maxQuantity ? parseInt(globalConfig.maxQuantity) : null,
                startDate: globalConfig.startDate || null,
                endDate: globalConfig.endDate || null,
                price: parseFloat(row.customPrice),
                currency: globalConfig.currency,
                priority: parseInt(globalConfig.priority),
                active: globalConfig.active,
                couponCodes: globalConfig.couponCodes ? globalConfig.couponCodes.split(',').map(c => c.trim()) : [],
                conditions: '' // ✅ CAMBIADO: De {} a ''
            };

            console.log('📤 Sending pricelist data:', dataToSend);
            return createPricelistSupplier(dataToSend);
        });

        await Promise.all(promises);
        
        setSuccessMessage(
            `✅ ${selectedRows.length} pricelist${selectedRows.length > 1 ? 's' : ''} created successfully!`
        );

        setTimeout(() => {
            onPricelistAdded();
            onClose();
            resetForm();
        }, 1500);
    } catch (error) {
        console.error('❌ Error saving pricelists:', error);
        setErrorMessage(error.response?.data?.message || 'Error saving pricelists');
    } finally {
        setLoading(false);
    }
};

    const resetForm = () => {
        setProductRows([]);
        setGlobalConfig({
            userid: '',
            supplierid: '',
            minQuantity: 1,
            maxQuantity: '',
            startDate: '',
            endDate: '',
            currency: '€',
            priority: 0,
            active: true,
            couponCodes: ''
        });
        setActiveStep(0);
        setSuccessMessage('');
        setErrorMessage('');
    };

    const selectedCount = productRows.filter(row => row.selected).length;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: '90%', md: 1100 } }
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* HEADER */}
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="h5" gutterBottom>
                        🏥 Bulk Supplier Pricing (Excel Style)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Step {activeStep + 1}: {activeStep === 0 ? 'Select products, set prices and assign doctors' : 'Configure global settings'}
                    </Typography>
                    
                    <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
                        <Step>
                            <StepLabel>Product Selection & Doctor Assignment</StepLabel>
                        </Step>
                        <Step>
                            <StepLabel>Global Configuration</StepLabel>
                        </Step>
                    </Stepper>
                </Box>

                <Divider />

                {successMessage && (
                    <Alert severity="success" sx={{ m: 2 }}>
                        {successMessage}
                    </Alert>
                )}

                {errorMessage && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                {/* CONTADOR */}
                <Box sx={{ mx: 3, mt: 2, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                    <Typography variant="subtitle2">
                        {selectedCount} product(s) selected | Ready to create {selectedCount} pricelist(s)
                    </Typography>
                </Box>

                {/* BOTONES DE EXPORT/IMPORT EXCEL */}
                {activeStep === 0 && (
                    <Box sx={{ mx: 3, mt: 2, display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={exportBlankTemplate}
                            disabled={selectedCount === 0 || loadingDoctors}
                        >
                            📥 Download Template ({selectedCount} products)
                        </Button>
                        
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadIcon />}
                            disabled={loading}
                        >
                            📤 Import from Excel
                            <input
                                type="file"
                                hidden
                                accept=".xlsx,.xls"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) importFromExcel(file);
                                    e.target.value = '';
                                }}
                            />
                        </Button>
                    </Box>
                )}

                {/* LOADING DOCTORS */}
                {loadingDoctors && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading doctors...</Typography>
                    </Box>
                )}

                {/* CONTENIDO PRINCIPAL */}
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {activeStep === 0 && (
                        <TableContainer component={Paper} sx={{ m: 3, flex: 1, overflow: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                indeterminate={selectedCount > 0 && selectedCount < productRows.length}
                                                checked={productRows.length > 0 && selectedCount === productRows.length}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell><strong>Code</strong></TableCell>
                                        <TableCell><strong>Product Name</strong></TableCell>
                                        <TableCell><strong>Original Price</strong></TableCell>
                                        <TableCell><strong>Custom Price *</strong></TableCell>
                                        <TableCell sx={{ minWidth: 200 }}><strong>Doctor *</strong></TableCell>
                                        <TableCell><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {productRows.map((row, index) => (
                                        <TableRow 
                                            key={row.productId}
                                            sx={{ 
                                                bgcolor: row.selected ? 'action.selected' : 'inherit',
                                                '&:hover': { bgcolor: 'action.hover' }
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={row.selected}
                                                    onChange={(e) => handleRowChange(index, 'selected', e.target.checked)}
                                                />
                                            </TableCell>
                                            <TableCell>{row.productCode}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ maxWidth: 250 }}>
                                                    {row.productName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>${row.originalPrice}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={row.customPrice}
                                                    onChange={(e) => handleRowChange(index, 'customPrice', e.target.value)}
                                                    placeholder="0.00"
                                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                                    sx={{ width: 120 }}
                                                    disabled={!row.selected}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormControl size="small" fullWidth>
                                                    <Select
                                                        value={row.doctorId}
                                                        onChange={(e) => handleRowChange(index, 'doctorId', e.target.value)}
                                                        displayEmpty
                                                        disabled={!row.selected || loadingDoctors}
                                                        MenuProps={{
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: 300,
                                                                    overflow: 'auto'
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="">
                                                            <em>
                                                                {loadingDoctors ? 'Loading...' : 'Select doctor...'}
                                                            </em>
                                                        </MenuItem>
                                                        {doctors.map(doctor => (
                                                            <MenuItem key={doctor._id} value={doctor._id}>
                                                                {doctor.name} {doctor.email && `(${doctor.email})`}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton 
                                                    size="small" 
                                                    color="error"
                                                    onClick={() => handleDeleteRow(index)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {activeStep === 1 && (
                        <Box sx={{ p: 3, overflow: 'auto' }}>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                These settings will apply to all {selectedCount} selected products
                            </Alert>

                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    ⚙️ Global Configuration
                                </Typography>
                                
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mt: 2 }}>
                                    <TextField
                                        label="Supplier ID (optional)"
                                        name="supplierid"
                                        value={globalConfig.supplierid}
                                        onChange={handleGlobalConfigChange}
                                        helperText="ObjectId of the supplier company"
                                        fullWidth
                                    />

                                    <FormControl fullWidth>
                                        <InputLabel>Currency</InputLabel>
                                        <Select
                                            name="currency"
                                            value={globalConfig.currency}
                                            onChange={handleGlobalConfigChange}
                                            label="Currency"
                                        >
                                            <MenuItem value="€">€ EUR</MenuItem>
                                            <MenuItem value="$">$ USD</MenuItem>
                                            <MenuItem value="£">£ GBP</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        type="number"
                                        label="Min Quantity *"
                                        name="minQuantity"
                                        value={globalConfig.minQuantity}
                                        onChange={handleGlobalConfigChange}
                                        InputProps={{ inputProps: { min: 1 } }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="number"
                                        label="Max Quantity (optional)"
                                        name="maxQuantity"
                                        value={globalConfig.maxQuantity}
                                        onChange={handleGlobalConfigChange}
                                        InputProps={{ inputProps: { min: 1 } }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="date"
                                        label="Start Date"
                                        name="startDate"
                                        value={globalConfig.startDate}
                                        onChange={handleGlobalConfigChange}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="date"
                                        label="End Date"
                                        name="endDate"
                                        value={globalConfig.endDate}
                                        onChange={handleGlobalConfigChange}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />

                                    <TextField
                                        type="number"
                                        label="Priority"
                                        name="priority"
                                        value={globalConfig.priority}
                                        onChange={handleGlobalConfigChange}
                                        helperText="Higher = more priority"
                                        InputProps={{ inputProps: { min: 0 } }}
                                        fullWidth
                                    />

                                    <TextField
                                        label="Coupon Codes"
                                        name="couponCodes"
                                        value={globalConfig.couponCodes}
                                        onChange={handleGlobalConfigChange}
                                        placeholder="CODE1, CODE2"
                                        helperText="Comma separated"
                                        fullWidth
                                    />
                                </Box>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={globalConfig.active}
                                            onChange={handleGlobalConfigChange}
                                            name="active"
                                            color="primary"
                                        />
                                    }
                                    label="Active"
                                    sx={{ mt: 2 }}
                                />
                            </Paper>
                        </Box>
                    )}
                </Box>

                {/* BOTONES DE NAVEGACIÓN */}
                <Divider />
                <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
                    {activeStep === 0 ? (
                        <>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => {
                                    onClose();
                                    resetForm();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleNext}
                                disabled={selectedCount === 0 || loadingDoctors}
                            >
                                Next: Configure Settings →
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={handleBack}
                                disabled={loading}
                            >
                                ← Back
                            </Button>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSubmit}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Creating...
                                    </>
                                ) : (
                                    `✅ Create ${selectedCount} Pricelist${selectedCount > 1 ? 's' : ''}`
                                )}
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default AddSupplierPriceDrawer;